import React from "react";
import PropTypes from "prop-types";
import { Icon, ICONS, StatusBadge, Pill } from "./ui";
import { checklistTone, checklistLabel } from "./data-utils";

/**
 * PS Kit validation summary — the band that sits above every section.
 *
 * It answers one question an ops user opens this tab to ask: what is still
 * blocking the PS Kit trigger? Only mandatory, not-complete items surface
 * here; the full picture lives behind "View Checklist".
 */
const PendingCard = ({ title, count, linkLabel, onLink, items }) => (
    <div className={"ops-ws__pend-card"}>
        <div className={"ops-ws__pend-head"}>
            <span className={"ops-ws__eyebrow"}>
                {title} · {count} pending
            </span>
            <button type={"button"} className={"ops-ws__link"} onClick={onLink}>
                {linkLabel}
                <Icon d={ICONS.arrow} size={12} width={2.4} />
            </button>
        </div>
        <div className={"ops-ws__pend-list"}>
            {items.map(item => (
                <button
                    key={item.id}
                    type={"button"}
                    className={"ops-ws__pend-row"}
                    onClick={onLink}
                    style={{ border: 0, background: "none", padding: 0, textAlign: "left", width: "100%" }}
                >
                    <StatusBadge tone={checklistTone(item.status)} small />
                    <span style={{ flex: 1, minWidth: 0 }}>
                        <span className={"ops-ws__pend-name"} style={{ display: "block" }}>
                            {item.name}
                            {item.applicant ? ` · ${item.applicant}` : ""}
                        </span>
                        <span className={"ops-ws__pend-reason"}>{item.reason}</span>
                    </span>
                    <Pill tone={checklistTone(item.status)}>
                        {checklistLabel(item.status)}
                    </Pill>
                </button>
            ))}
        </div>
    </div>
);

PendingCard.propTypes = {
    title: PropTypes.string,
    count: PropTypes.number,
    linkLabel: PropTypes.string,
    onLink: PropTypes.func,
    items: PropTypes.array
};

// Cap the band so a case with dozens of gaps does not push the page down;
// the drawer carries the rest.
const MAX_ROWS = 5;

const SummaryBand = ({
    formPending,
    docPending,
    onOpenChecklist,
    onGoForm,
    onGoDocs
}) => {
    const total = formPending.length + docPending.length;
    return (
        <div className={"ops-ws__summary" + (total === 0 ? " is-clear" : "")}>
            <div className={"ops-ws__summary-head"}>
                <div style={{ flex: "1 1 320px", minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                        <span className={"ops-ws__summary-title"}>
                            PS Kit validation summary
                        </span>
                        <Pill tone={total === 0 ? "ok" : "warn"}>
                            {total === 0 ? "All clear" : `${total} pending`}
                        </Pill>
                    </div>
                    <div className={"ops-ws__summary-note"}>
                        {total === 0
                            ? "Every mandatory form field and document is clear — the kit can be triggered."
                            : `${total} mandatory item${total > 1 ? "s" : ""} need action before the PS Kit can be triggered.`}
                    </div>
                </div>
                <button
                    type={"button"}
                    className={"ops-ws__btn"}
                    onClick={onOpenChecklist}
                >
                    <Icon d={ICONS.checklist} size={14} />
                    View Checklist
                </button>
            </div>

            {total > 0 ? (
                <div className={"ops-ws__summary-grid"}>
                    {formPending.length ? (
                        <PendingCard
                            title={"Form fields"}
                            count={formPending.length}
                            linkLabel={"Go to Case reference"}
                            onLink={onGoForm}
                            items={formPending.slice(0, MAX_ROWS)}
                        />
                    ) : null}
                    {docPending.length ? (
                        <PendingCard
                            title={"Documents"}
                            count={docPending.length}
                            linkLabel={"Go to Documents"}
                            onLink={onGoDocs}
                            items={docPending.slice(0, MAX_ROWS)}
                        />
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

SummaryBand.propTypes = {
    formPending: PropTypes.array.isRequired,
    docPending: PropTypes.array.isRequired,
    onOpenChecklist: PropTypes.func.isRequired,
    onGoForm: PropTypes.func.isRequired,
    onGoDocs: PropTypes.func.isRequired
};

export default SummaryBand;
